// Structured summary panel + TTS readback
// (docs/product-specs/summary.md).

import { html, LitElement } from "lit";
import { ApiError, meetings, summarySpeechUrl } from "../lib/api";
import { parseSummaryBody, type SummaryRow } from "../lib/types";

export class MmSummary extends LitElement {
  static override properties = {
    meetingId: { attribute: false },
    summary: { attribute: false },
  };

  meetingId = 0;
  summary: SummaryRow | null = null;
  private audioUrl: string | null = null;
  private speaking = false;
  private error = "";

  override createRenderRoot(): HTMLElement {
    return this;
  }

  private async listen(): Promise<void> {
    this.speaking = true;
    this.error = "";
    this.requestUpdate();
    try {
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = await summarySpeechUrl(this.meetingId);
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "speech failed";
    } finally {
      this.speaking = false;
      this.requestUpdate();
    }
  }

  private async regenerate(): Promise<void> {
    this.error = "";
    try {
      await meetings.summarize(this.meetingId);
      this.dispatchEvent(new CustomEvent("summary-regenerating", { bubbles: true }));
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "regenerate failed";
      this.requestUpdate();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
  }

  override render() {
    const row = this.summary;
    const body = parseSummaryBody(row);
    if (!row || row.status === "pending") {
      return html`<p class="empty">No summary yet.</p>`;
    }
    if (row.status === "running") {
      return html`<div class="message-card message-card-info">
        <p>Summarizing… this updates automatically.</p>
      </div>`;
    }
    return html`
      ${row.status === "failed"
        ? html`<div class="message-card message-card-warn">
            <p>
              Summary failed: <span class="mono">${row.error ?? "unknown"}</span>
              ${body ? " — showing the previous summary." : ""}
            </p>
          </div>`
        : ""}
      ${this.error
        ? html`<div class="message-card message-card-error"><p>${this.error}</p></div>`
        : ""}
      ${body
        ? html`
            <p>${body.overview}</p>
            ${body.key_points.length
              ? html`<h3>Key points</h3>
                  <ul class="summary-list">
                    ${body.key_points.map((k) => html`<li>${k}</li>`)}
                  </ul>`
              : ""}
            ${body.decisions.length
              ? html`<h3>Decisions</h3>
                  <ul class="summary-list">
                    ${body.decisions.map((d) => html`<li>${d}</li>`)}
                  </ul>`
              : ""}
            ${body.action_items.length
              ? html`<h3>Action items</h3>
                  <ul class="summary-list">
                    ${body.action_items.map(
                      (a) => html`<li class="action-check">
                        <input type="checkbox" disabled />
                        <span>
                          ${a.text}
                          ${a.owner
                            ? html` <span class="badge badge-accent">${a.owner}</span>`
                            : ""}
                        </span>
                      </li>`,
                    )}
                  </ul>`
              : ""}
            <div class="toolbar">
              <button
                class="ghost-button"
                ?disabled=${this.speaking}
                @click=${this.listen}
              >
                ${this.speaking ? "Generating audio…" : "🔊 Listen"}
              </button>
              <button class="ghost-button" @click=${this.regenerate}>
                Regenerate
              </button>
            </div>
            ${this.audioUrl
              ? html`<audio
                  controls
                  autoplay
                  src=${this.audioUrl}
                  style="width: 100%; margin-top: 0.75rem"
                ></audio>`
              : ""}
          `
        : html`<p class="empty">Summary unavailable.</p>
            <button class="ghost-button" @click=${this.regenerate}>
              Try again
            </button>`}
    `;
  }
}

customElements.define("mm-summary", MmSummary);
